import { Component, OnInit } from "@angular/core";
import { FormControl, Validators } from "@angular/forms";
import { MatDialogRef } from "@angular/material/dialog";
import { ClientService } from "../client.service";
import { UserAdminsService } from "../../user-admins/user-admins.service";
import { UserRole } from "../../user-admins/user-admins.interface";
import { ToastrService } from "../../shared/toastr.service";
import { TranslateService } from "@ngx-translate/core";
import { ShowOnSubmitAttemptErrorStateMatcher } from "../../shared/error-state-matcher";

@Component({
  selector: "app-super-admin-dialog",
  templateUrl: "./super-admin-dialog.component.html",
  styleUrls: ["./super-admin-dialog.component.scss"],
  standalone: false,
})
export class SuperAdminDialogComponent implements OnInit {
  superAdmins: UserRole[] = [];
  readonly adminLabel = (admin: UserRole) => admin.email;
  readonly adminValue = (admin: UserRole) => admin.email;
  selectedAdminControl = new FormControl("", [(c) => Validators.required(c)]);
  newAdminEmailControl = new FormControl("", [
    (c) => Validators.required(c),
    (c) => Validators.email(c),
  ]);
  createErrorStateMatcher = new ShowOnSubmitAttemptErrorStateMatcher();
  removeErrorStateMatcher = new ShowOnSubmitAttemptErrorStateMatcher();
  loading = false;
  processing = false;

  constructor(
    public dialogRef: MatDialogRef<SuperAdminDialogComponent>,
    private clientService: ClientService,
    private userAdminsService: UserAdminsService,
    private toastr: ToastrService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.loadSuperAdmins();
  }

  loadSuperAdmins(): void {
    this.loading = true;
    this.userAdminsService.fetchAdmins().subscribe({
      next: (admins) => {
        const excludedEmails = [
          "dawid.bieniek@gsignature.com",
          "marek.stopka@gsignature.com",
        ];
        this.superAdmins = admins.filter(
          (admin) =>
            admin.role === "SUPER_ADMIN" &&
            !excludedEmails.includes(admin.email),
        );
        this.loading = false;
      },
      error: () => {
        this.toastr.error(
          this.translate.instant("superAdminDialog.loadError") as string,
        );
        this.loading = false;
      },
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  createNewAdmin(): void {
    this.createErrorStateMatcher.submitAttempted = true;
    if (this.newAdminEmailControl.invalid) {
      return;
    }

    this.processing = true;
    const email = this.newAdminEmailControl.value;

    this.clientService.assignSuperAdminToAllDomains(email).subscribe({
      next: () => {
        this.toastr.success(
          this.translate.instant("superAdminDialog.createSuccess") as string,
        );
        this.newAdminEmailControl.reset();
        this.processing = false;
        this.loadSuperAdmins();
      },
      error: () => {
        this.toastr.error(
          this.translate.instant("superAdminDialog.createError") as string,
        );
        this.processing = false;
      },
    });
  }

  removeFromAll(): void {
    this.removeErrorStateMatcher.submitAttempted = true;
    if (this.selectedAdminControl.invalid) {
      return;
    }

    this.processing = true;
    const email = this.selectedAdminControl.value;

    this.clientService.removeSuperAdminFromAllDomains(email).subscribe({
      next: () => {
        this.toastr.success(
          this.translate.instant("superAdminDialog.removeSuccess") as string,
        );
        this.selectedAdminControl.reset();
        this.processing = false;
        this.loadSuperAdmins();
      },
      error: () => {
        this.toastr.error(
          this.translate.instant("superAdminDialog.removeError") as string,
        );
        this.processing = false;
      },
    });
  }
}
